import { AlertTriangle } from 'lucide-react';
import { Badge } from '../../ui';
import { formatOutputValue } from '../../features/estimation/estimateModel';
import type { EvaluationResult } from '../../features/estimation/engine';
import type { OutputDef } from '../../features/templates/types';

export interface OutputsPreviewProps {
  /** The template's output definitions (for labels + declaration order). */
  defs: OutputDef[];
  /** The live evaluation result (outputs + per-output errors). */
  result: EvaluationResult;
  /** Compact mode renders smaller cells (used inside the wizard sidebar). */
  compact?: boolean;
}

/**
 * Renders the live-computed outputs of an estimate. Successful outputs show the
 * formatted value; failing outputs show the engine's error message. Reflects
 * the pure {@link EvaluationResult} — it performs no evaluation itself.
 */
export function OutputsPreview({ defs, result, compact }: OutputsPreviewProps) {
  if (defs.length === 0) {
    return (
      <p className="text-sm text-zinc-500">
        This template defines no outputs. Add output expressions to the template
        to compute results.
      </p>
    );
  }

  return (
    <div className={compact ? 'flex flex-col gap-2' : 'grid gap-3 sm:grid-cols-2'}>
      {defs.map((def) => {
        const error = result.errors[def.key];
        const hasValue = Object.prototype.hasOwnProperty.call(
          result.outputs,
          def.key,
        );
        return (
          <div
            key={def.key}
            className="rounded-xl border border-surface-border bg-surface-1 px-4 py-3"
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-medium text-zinc-400">
                {def.label || def.key}
              </span>
              {error ? (
                <Badge tone="danger" dot>
                  Error
                </Badge>
              ) : null}
            </div>
            {error ? (
              <p className="mt-1 flex items-start gap-1.5 text-xs text-red-400">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                {error}
              </p>
            ) : (
              <p className="mt-1 text-lg font-semibold tabular-nums text-zinc-50">
                {hasValue ? formatOutputValue(result.outputs[def.key]) : '—'}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default OutputsPreview;
